import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from '../auth/auth.service';
import { PermissionArea } from '../auth/auth.type';
import { Header } from '../shared/header/header';
import { ProxyService } from '../proxy/proxy.service';
import { PROXY_ALERT_GB, ProxyUsage } from '../proxy/proxy.type';
import { ContractBalanceWidget } from '../player-auction/contract-balance/contract-balance';

interface DashboardLink {
  title: string;
  description: string;
  icon: string;
  route: string;
  visible: boolean;
}

interface DashboardSection {
  title: string;
  icon: string;
  area: PermissionArea;
  links: DashboardLink[];
}

@Component({
  selector: 'app-dashboard',
  imports: [
    CommonModule,
    RouterLink,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatTooltipModule,
    Header,
    ContractBalanceWidget,
  ],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss'
})
export class Dashboard implements OnInit {
  private authService = inject(AuthService);
  private proxyService = inject(ProxyService);

  proxyUsage: ProxyUsage | null = null;
  sections: DashboardSection[] = [];

  ngOnInit(): void {
    this.sections = this.buildSections();
    if (this.isAdmin && this.authService.canRead('system')) {
      this.proxyService.getProxyUsage().subscribe({
        next: usage => this.proxyUsage = usage,
        error: () => this.proxyUsage = null,
      });
    }
  }

  get isAdmin(): boolean {
    return this.authService.hasAnyRole('Admin', 'SuperAdmin');
  }

  get showContractBalance(): boolean {
    return this.isAdmin && this.authService.canRead('contracts');
  }

  get username(): string {
    return this.authService.getCurrentUser()?.username ?? '';
  }

  get proxyAlert(): boolean {
    return !!this.proxyUsage && (this.proxyUsage.low_balance || this.proxyUsage.gb_remaining <= PROXY_ALERT_GB);
  }

  logout(): void {
    this.authService.logout();
  }

  /** Same visibility rules as the route guards, so a visible tile never bounces back here. */
  private buildSections(): DashboardSection[] {
    const auth = this.authService;
    const isAdmin = this.isAdmin;
    const isSuperAdmin = auth.hasRole('SuperAdmin');
    const isSupplier = auth.hasRole('Supplier');
    const isConsumer = auth.hasRole('Consumer');
    const hasAuction = auth.hasRole('AuctionAccess');

    const sections: DashboardSection[] = [
      {
        title: 'Contracts',
        icon: 'swap_horiz',
        area: 'contracts',
        links: [
          {
            title: 'Orders',
            description: 'Sell contracts: create, track and settle orders',
            icon: 'receipt',
            route: '/player-auction',
            visible: isAdmin || (isConsumer && hasAuction),
          },
          {
            title: 'Buy Contracts',
            description: 'Supplier buy contracts and auto-buy',
            icon: 'shopping_cart',
            route: '/player-auction/buy-contract',
            visible: isAdmin || (hasAuction && (isConsumer || isSupplier)),
          },
          {
            title: 'Stocks',
            description: 'Stock accounts, readiness and credit',
            icon: 'inventory_2',
            route: '/stocks',
            visible: isAdmin || isSupplier,
          },
        ],
      },
      {
        title: 'Suppliers & payments',
        icon: 'groups',
        area: 'suppliers_payments',
        links: [
          {
            title: 'Suppliers',
            description: 'Priorities, manual-contract limits and status',
            icon: 'groups',
            route: '/suppliers',
            visible: isAdmin,
          },
          {
            title: 'Supplier Settings',
            description: 'Default fees, prices and portal configuration',
            icon: 'tune',
            route: '/supplier-settings',
            visible: isAdmin,
          },
          {
            title: 'Payments',
            description: 'Settle runs and contracts owed to suppliers',
            icon: 'account_balance_wallet',
            route: '/payments',
            visible: isAdmin,
          },
          {
            title: 'Accounting',
            description: 'Export to Hesabfa and compare the books',
            icon: 'menu_book',
            route: '/accounting',
            visible: isAdmin,
          },
        ],
      },
      {
        title: 'System',
        icon: 'settings',
        area: 'system',
        links: [
          {
            title: 'Players',
            description: 'FIFA player database and prices',
            icon: 'sports_soccer',
            route: '/players',
            visible: !isSupplier && !isConsumer,
          },
          {
            title: 'Proxies',
            description: 'Proxy configurations and usage',
            icon: 'dns',
            route: '/proxies',
            visible: isAdmin,
          },
          {
            title: 'Settings',
            description: 'Global configuration for listing, buying and payments',
            icon: 'settings',
            route: '/settings',
            visible: isAdmin,
          },
          {
            title: 'Admin Panel',
            description: 'Users, roles and API keys',
            icon: 'admin_panel_settings',
            route: '/users',
            visible: isSuperAdmin,
          },
        ],
      },
      {
        title: 'Accounts & reports',
        icon: 'insights',
        area: 'accounts_reports',
        links: [
          {
            title: 'Login Info',
            description: 'Account credentials and cookies',
            icon: 'key',
            route: '/login-info',
            visible: true,
          },
          {
            title: 'Reports',
            description: 'Daily coin totals, paid and unpaid',
            icon: 'receipt_long',
            route: '/reports',
            visible: isAdmin,
          },
        ],
      },
    ];

    return sections
      .filter(s => auth.canRead(s.area))
      .map(s => ({ ...s, links: s.links.filter(l => l.visible) }))
      .filter(s => s.links.length > 0);
  }
}
